import { Injectable } from "@angular/core";
import { Actions, concatLatestFrom, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { ActionWithPayload } from "@obg/ngrx-utils";
import {
	AppError,
	DOCUMENT_META_DATA_ACTION_TYPE,
	DocumentMetaData,
	DocumentMetaService,
	MarketSelector,
	MessageScopeType,
	MessageService,
	MessageType,
	PageSelector2,
} from "@obg/ngx.core";
import { ignoreEmpty, ignoreNil } from "@obg/ngx.global";
import { RouteSelector } from "@obg/ngx.router";
import { combineLatest, merge, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

import { CoreContentState } from "../state";

import { FLAG_ACTION_TYPE, FlagAction } from "./flag.action";
import { flagContext } from "./flag.const";
import { Flag, UpdatePayload } from "./flag.model";
import { FlagSelector } from "./flag.selector";
import { FlagService } from "./flag.service";

/**
 * @deprecated [fabric-deprecated] - Use fabric FlagService from svc-content instead
 */
@Injectable()
export class FlagEffect {
	/**
	 * @deprecated [fabric-deprecated] - Use "document.obgEventBus.retrieve(ContentValue.getAllFlags_v2)" instead
	 */
	getAll$ = createEffect(() =>
		this.actions$.pipe(
			ofType(FLAG_ACTION_TYPE.getAll),
			switchMap(() =>
				this.service.getAll$().pipe(
					map((flags) => this.action.getAllSuccess(flags)),
					catchError((error: AppError) => of(this.action.getAllFail(error)))
				)
			)
		)
	);

	update$ = createEffect(() =>
		this.actions$.pipe(
			ofType(FLAG_ACTION_TYPE.update),
			map((action: ActionWithPayload<UpdatePayload>) => {
				const flags = this.service.appendHrefToFlags(action.payload);
				return this.action.updateSuccess(flags);
			})
		)
	);

	documentSet$ = createEffect(() =>
		combineLatest([
			this.store.select(FlagSelector.getInitialFlags).pipe(ignoreEmpty()),
			this.store.select(PageSelector2.getCurrent).pipe(ignoreNil()),
			this.store.select(RouteSelector.getQueryParams),
		]).pipe(
			map(([flags, page, queryParams]) =>
				this.action.update({
					flags,
					urls: this.documentMetaService.generateAlternativeUrls(page!, false),
					queryParams,
				})
			)
		)
	);

	updateFlag$ = createEffect(() =>
		this.actions$.pipe(
			ofType(DOCUMENT_META_DATA_ACTION_TYPE.set),
			map((x: ActionWithPayload<DocumentMetaData>) => x.payload.alternativeUrls),
			ignoreEmpty(),
			concatLatestFrom(() => [
				this.store.select(FlagSelector.getInitialFlags),
				this.store.select(RouteSelector.getQueryParams),
			]),
			map(([alternativeUrls, flags, queryParams]) =>
				this.action.update({
					flags,
					urls: alternativeUrls,
					queryParams,
				})
			)
		)
	);

	updateSuccess$ = createEffect(() =>
		this.actions$.pipe(
			ofType(FLAG_ACTION_TYPE.updateSuccess),
			map((action: ActionWithPayload<Flag[]>) => action.payload),
			concatLatestFrom(() => this.store.select(this.marketSelector.getCurrent())),
			map(([flags, currentMarket]) => this.service.getByMarket(flags, currentMarket)),
			ignoreNil(),
			map((flag) => this.action.setCurrent(flag))
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$(
				{
					[FLAG_ACTION_TYPE.getAllFail]: flagContext,
				},
				MessageScopeType.Global,
				MessageType.Error
			),
			this.messageService.mapClearByContext$({
				[FLAG_ACTION_TYPE.getAll]: flagContext,
			})
		)
	);

	constructor(
		private actions$: Actions,
		private action: FlagAction,
		private service: FlagService,
		private store: Store<CoreContentState>,
		private marketSelector: MarketSelector,
		private messageService: MessageService,
		private documentMetaService: DocumentMetaService
	) {}
}
